import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = [
    'stickyBar',
    'addToCartButton',
    'quantityDisplay',
    'quantityNumber',
  ];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');

    target?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate,
      { signal }
    );

    target?.addEventListener(
      ThemeEvents.variantSelected,
      this.#handleVariantSelected,
      { signal }
    );

    document.addEventListener(
      ThemeEvents.cartUpdate,
      this.#handleCartAddComplete,
      { signal }
    );

    document.addEventListener(
      ThemeEvents.cartError,
      this.#handleCartAddComplete,
      { signal }
    );

    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#handleQuantityUpdate,
      { signal }
    );

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();

    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();

    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');

    if (!buyButtonsBlock) return;

    const footer =
      document.querySelector('footer') ??
      document.querySelector('[class*="footer-group"]');

    if (!footer) return;

    /*
     * Observer for buy buttons visibility
     */
    this.#buyButtonsIntersectionObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;

        if (!entry) return;

        /*
         * Only show sticky bar if buy buttons have been
         * scrolled past above the viewport.
         */
        if (!entry.isIntersecting && !this.#isStuck) {
          const rect = entry.target.getBoundingClientRect();

          if (rect.bottom < 0 || rect.top < 0) {
            this.#showStickyBar();
          }
        } else if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = false;
          this.#hideStickyBar();
        }
      }
    );

    /*
     * Observer for footer visibility.
     * Hide sticky bar when user reaches page bottom.
     */
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;

        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          const rect = buyButtonsBlock.getBoundingClientRect();

          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);

    this.#targetAddToCartButton = productForm.querySelector(
      '[ref="addToCartButton"]'
    );
  }

  /**
   * Handles sticky button click.
   *
   * First click:
   * Add selected product to cart.
   *
   * After successful cart update:
   * Button becomes GO TO CHECKOUT.
   *
   * Second click:
   * Go directly to Shopify checkout.
   */
  handleAddToCartClick = async () => {
    const stickyButton = this.refs.addToCartButton;

    /*
     * Product was already successfully added.
     * Send customer directly to checkout.
     */
    if (stickyButton.dataset.checkout === 'true') {
      window.location.href = '/checkout';
      return;
    }

    if (!this.#targetAddToCartButton) return;

    /*
     * Trigger the actual product-page Add to Cart button.
     */
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();

    /*
     * Preserve existing fly-to-cart animation.
     */
    const cartIcon = document.querySelector(
      '.header-actions__cart-icon'
    );

    if (
      !cartIcon ||
      !stickyButton ||
      !this.refs.productImage
    ) {
      return;
    }

    const flyToCartElement =
      /** @type {FlyToCart} */ (
        document.createElement('fly-to-cart')
      );

    flyToCartElement.classList.add(
      'fly-to-cart--sticky'
    );

    flyToCartElement.style.setProperty(
      'background-image',
      `url(${this.refs.productImage.src})`
    );

    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([
      stickyButton,
      flyToCartElement,
    ]);
  };

  /**
   * Handles variant update events
   *
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {
    if (
      event.detail.data.productId !==
      this.dataset.productId
    ) {
      return;
    }

    const variant = event.detail.resource;

    const newStickyAddToCart =
      event.detail.data.html.querySelector(
        'sticky-add-to-cart'
      );

    if (!newStickyAddToCart) return;

    const newStickyBar =
      newStickyAddToCart.querySelector(
        '[ref="stickyBar"]'
      );

    if (!newStickyBar) return;

    /*
     * Store current sticky visibility before morph.
     */
    const currentStuck =
      this.refs.stickyBar.getAttribute(
        'data-stuck'
      ) || 'false';

    const variantAvailable =
      newStickyAddToCart.dataset.variantAvailable;

    /*
     * Morph sticky content to selected variant.
     */
    morph(
      this.refs.stickyBar,
      newStickyBar,
      { childrenOnly: true }
    );

    /*
     * Restore visibility.
     */
    this.refs.stickyBar.setAttribute(
      'data-stuck',
      currentStuck
    );

    this.dataset.variantAvailable =
      variantAvailable;

    if (variant && variant.id) {
      this.dataset.currentVariantId =
        variant.id;
    }

    /*
     * Re-cache actual product page Add to Cart button.
     */
    const productForm = this.#getProductForm();

    if (productForm) {
      this.#targetAddToCartButton =
        productForm.querySelector(
          '[ref="addToCartButton"]'
        );
    }

    /*
     * A variant change means we're back in
     * ADD TO CART mode.
     */
    if (this.refs.addToCartButton) {
      this.refs.addToCartButton.removeAttribute(
        'data-checkout'
      );
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }

    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   *
   * @param {CustomEvent} event
   */
  #handleVariantSelected = (event) => {
    const variantId =
      event.detail.resource?.id;

    if (!variantId) return;

    this.dataset.currentVariantId =
      variantId;
  };

  /**
   * Updates the variant title when selected
   * options do not correspond to an available variant.
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';

    const variantTitleElement =
      this.querySelector(
        '.sticky-add-to-cart__variant'
      );

    const productId =
      this.dataset.productId;

    const variantPicker =
      document.querySelector(
        `variant-picker[data-product-id="${productId}"]`
      );

    if (
      !variantTitleElement ||
      !variantPicker
    ) {
      return;
    }

    const selectedOptions =
      Array.from(
        variantPicker.querySelectorAll(
          'input:checked'
        )
      )
        .map(
          (option) =>
            /** @type {HTMLInputElement} */ (
              option
            ).value
        )
        .filter((value) => value !== '')
        .join(' / ');

    if (!selectedOptions) return;

    variantTitleElement.textContent =
      selectedOptions;
  };

  /**
   * Handles successful/error cart operation.
   *
   * Successful add:
   * convert sticky CTA into GO TO CHECKOUT.
   *
   * Error:
   * leave button as Add to Cart.
   *
   * @param {CustomEvent} event
   */
  #handleCartAddComplete = (event) => {
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet =
        'false';
    }

    /*
     * Do NOT turn into checkout on cart errors.
     */
    if (event.type !== ThemeEvents.cartUpdate) {
      return;
    }

    const button =
      this.refs.addToCartButton;

    if (!button) return;

    /*
     * Mark sticky button as checkout mode.
     */
    button.dataset.checkout = 'true';

    /*
     * Remove Shopify's temporary checkmark state.
     */
    button.removeAttribute('data-added');

    /*
     * Replace sticky CTA text.
     */
    button.innerHTML =
      '<span>GO TO CHECKOUT →</span>';
  };

  /**
   * Handles quantity selector updates.
   *
   * @param {QuantitySelectorUpdateEvent} event
   */
  #handleQuantityUpdate = (event) => {
    /*
     * Ignore quantity changes from cart drawer.
     */
    if (event.detail.cartLine) return;

    this.#currentQuantity =
      event.detail.quantity;

    this.#updateButtonText();
  };

  /**
   * Shows sticky bar.
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;

    this.#isStuck = true;

    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides sticky bar.
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;

    this.#isStuck = false;

    stickyBar.dataset.stuck = 'false';
  }

  /**
   * Gets the product form element.
   *
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId =
      this.dataset.productId;

    if (!productId) return null;

    const sectionElement =
      this.closest('.shopify-section');

    if (!sectionElement) return null;

    const sectionId =
      sectionElement.id.replace(
        'shopify-section-',
        ''
      );

    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets initial quantity.
   */
  #getInitialQuantity() {
    this.#currentQuantity =
      parseInt(
        this.dataset.initialQuantity || '1'
      ) || 1;

    this.#updateButtonText();
  }

  /**
   * Updates quantity display.
   */
  #updateButtonText() {
    const {
      addToCartButton,
      quantityDisplay,
      quantityNumber,
    } = this.refs;

    const available =
      !addToCartButton.disabled;

    quantityNumber.textContent =
      this.#currentQuantity.toString();

    if (
      available &&
      this.#currentQuantity > 1
    ) {
      quantityDisplay.style.display =
        'inline';
    } else {
      quantityDisplay.style.display =
        'none';
    }
  }
}

if (
  !customElements.get(
    'sticky-add-to-cart'
  )
) {
  customElements.define(
    'sticky-add-to-cart',
    StickyAddToCartComponent
  );
}